import { Box } from '@kiwicom/orbit-components';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

type StepProps = {
  number: number;
  translatedComponent: ReactNode;
  isLast?: boolean;
};
const Step = ({ number, translatedComponent, isLast = false }: StepProps) => (
  <Box
    display="flex"
    direction="row"
    align="center"
    className="relative"
    margin={{ bottom: '600' }}
    width="100%"
  >
    <Box
      className={`rounded-full border z-10 flex items-center justify-center w-[24px] h-[24px] p-400 bg-refer-friend-background ${
        number === 1
          ? 'border-refer-friend-purple text-refer-friend-purple [box-shadow:0_0_0_6px_rgba(64,0,130,0.35)]'
          : 'border-ink-dark text-ink-dark bg-refer-friend-background'
      }`}
      position="relative"
    >
      <span className="text-large font-bold">{number}</span>
    </Box>

    <Box margin={{ left: '200' }} tablet={{ margin: { left: '400' } }}>
      <span className={`text-normal ${number === 1 ? 'text-refer-friend-purple' : ''}`}>
        {translatedComponent}
      </span>
    </Box>

    {!isLast && (
      <div className="absolute left-400 top-800 z-0 w-px h-[calc(100%+18px)] border border-dashed border-ink-dark border-opacity-70" />
    )}
  </Box>
);

const TimelineComponent = ({
  refereeVoucherAmount,
  maximumRefererCreditTotal,
  firstBookingWithinDays,
}) => {
  const steps = [
    {
      number: 1,
      translatedComponent: (
        <FormattedMessage
          id="account_v2.refer_friend.invitee_intro.timeline.step1"
          defaultMessage="Create your Kiwi.com account with this invitation link."
          description="Designer: Anja Bošnjak Mihić. Figma: https://www.figma.com/design/j5u71n6wRBklvXBlvlRx5w/Refer-a-friend-%5B2025%5D?node-id=1086-47681&t=YL3pXwtKCvp5NYiH-0"
        />
      ),
    },
    {
      number: 2,
      translatedComponent: (
        <FormattedMessage
          id="account_v2.refer_friend.invitee_intro.timeline.step2"
          defaultMessage="We'll give you a {inviteeVoucherAmount} voucher that you can use within the next {firstBookingWithinDays} days"
          values={{
            inviteeVoucherAmount: refereeVoucherAmount,
            firstBookingWithinDays,
          }}
          description="Designer: Anja Bošnjak Mihić. Figma: https://www.figma.com/design/j5u71n6wRBklvXBlvlRx5w/Refer-a-friend-%5B2025%5D?node-id=1086-47681&t=YL3pXwtKCvp5NYiH-0"
        />
      ),
    },
    {
      number: 3,
      translatedComponent: (
        <FormattedMessage
          id="account_v2.refer_friend.invitee_intro.timeline.step3"
          defaultMessage="Book a flight to your dream destination, and use your voucher to save on your trip."
          description="Designer: Anja Bošnjak Mihić. Figma: https://www.figma.com/design/j5u71n6wRBklvXBlvlRx5w/Refer-a-friend-%5B2025%5D?node-id=1086-47681&t=YL3pXwtKCvp5NYiH-0"
        />
      ),
    },
    {
      number: 4,
      translatedComponent: (
        <FormattedMessage
          id="account_v2.refer_friend.invitee_intro.timeline.step4"
          defaultMessage="Once you've booked, your friend will get their reward for referring you."
          description="Designer: Anja Bošnjak Mihić. Figma: https://www.figma.com/design/j5u71n6wRBklvXBlvlRx5w/Refer-a-friend-%5B2025%5D?node-id=1086-47681&t=YL3pXwtKCvp5NYiH-0"
        />
      ),
    },
    {
      number: 5,
      translatedComponent: (
        <FormattedMessage
          id="account_v2.refer_friend.invitee_intro.timeline.step5"
          defaultMessage="To earn your own rewards, invite your friends to get up to {maximumInviterCreditTotal} in Credit."
          values={{ maximumInviterCreditTotal: maximumRefererCreditTotal }}
          description="Designer: Anja Bošnjak Mihić. Figma: https://www.figma.com/design/j5u71n6wRBklvXBlvlRx5w/Refer-a-friend-%5B2025%5D?node-id=1086-47681&t=YL3pXwtKCvp5NYiH-0"
        />
      ),
      isLast: true,
    },
  ];

  return (
    <Box
      display="flex"
      direction="column"
      className="justify-between items-start relative max-h-[60vh] pb-[12vh] overflow-y-auto tb:overflow-visible tb:max-h-none tb:pb-0 tb:mb-0 z-0 tb:z-10"
      padding={{ right: '400', left: '400' }}
      tablet={{
        padding: { right: 'none', left: 'none' },
        margin: { bottom: '600' },
      }}
      dataTest="ReferFriendTimeline"
    >
      {steps.map((step) => (
        <Step
          key={step.number}
          number={step.number}
          translatedComponent={step.translatedComponent}
          isLast={step.isLast}
        />
      ))}

      <Box className="fixed bottom-0 left-0 right-0 h-[20vh] bg-gradient-to-t  from-refer-friend-background to-transparent pointer-events-none tb:hidden" />
    </Box>
  );
};

export default TimelineComponent;
